import type { ReactNode } from "react"

import { useHasPermission } from "../../access/use-access"
import { Button } from "../../ui"
import { StandHeader } from "../directories/stand"
import styles from "./branch-screen.module.css"

/**
 * «Настройки» — перечень подразделов, а не заглушка.
 *
 * ПОЧЕМУ ЭТО ВАЖНЕЕ, ЧЕМ КАЖЕТСЯ. Правила записи и настройка доверия уже работают, но
 * жили на адресах, которых никто не знал: раздел «Настройки» отвечал «появятся позже»
 * и этим прятал готовое. Владелец делал вывод, что настроить нельзя ничего, и звонил
 * спрашивать то, что у него уже есть.
 *
 * Подраздел, к которому нет права, здесь НЕ показывается: перечень — это навигация,
 * а не витрина возможностей, и упираться в отказ на каждом втором пункте незачем.
 */
export function SettingsScreen(props: {
  readonly onOpen: (
    path:
      | "/settings/booking-rules"
      | "/settings/staff-visibility"
      | "/settings/templates"
      | "/settings/notifications"
      | "/settings/roles",
  ) => void
}): ReactNode {
  const canSeeRules = useHasPermission("settings.view")
  const canSeeVisibility = useHasPermission("settings.view")
  /*
   * ШАБЛОНЫ — ПОД СВОИМ ПРАВОМ, А НЕ ПОД `settings.view`. Текст, который прочитает
   * клиент, правит не всякий, кто может посмотреть настройки: администратор точки видит
   * правила записи, но переписывать голос салона — решение управляющего. Право уже есть
   * в каталоге, и здесь спрашивается ровно оно, чтобы карточка не вела в отказ 403.
   */
  const canSeeTemplates = useHasPermission("templates.manage")
  /*
   * МАТРИЦА — ПОД СВОИМ ПРАВОМ. Экран сделан пакетом П1-15 и жил на адресе, которого
   * никто не знал: разбор нашёл, что на /settings/notifications нет ни одной ссылки
   * во всём кабинете. Готовый экран, до которого нельзя дойти, для владельца
   * неотличим от несделанного — он звонит спрашивать то, что у него уже есть.
   */
  const canSeeNotifications = useHasPermission("notifications.rules.manage")
  const canManageRoles = useHasPermission("roles.manage")

  return (
    <div className={styles["screen"]}>
      <StandHeader title="Настройки" repeatsSection />

      <div className={styles["column"]}>
        {canSeeRules ? (
          <div className={styles["block"]}>
            <div className={styles["blockTitle"]}>Правила записи</div>
            <p className={styles["consequence"]}>
              За сколько клиент может записаться и отменить, как долго держится
              освободившееся время, что делать при неявках.
            </p>
            <div className={styles["chips"]}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  props.onOpen("/settings/booking-rules")
                }}
              >
                Открыть
              </Button>
            </div>
          </div>
        ) : null}

        {canSeeVisibility ? (
          <div className={styles["block"]}>
            <div className={styles["blockTitle"]}>Что мастер видит о клиенте</div>
            <p className={styles["consequence"]}>
              Телефон клиента и деньги в карточке визита. Здесь же журнал открытий
              телефона: кто открывал номера и скольких клиентов.
            </p>
            <div className={styles["chips"]}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  props.onOpen("/settings/staff-visibility")
                }}
              >
                Открыть
              </Button>
            </div>
          </div>
        ) : null}

        {canSeeNotifications ? (
          <div className={styles["block"]}>
            <div className={styles["blockTitle"]}>Уведомления клиентам</div>
            <p className={styles["consequence"]}>
              Какие события отправлять клиенту и какими каналами: подтверждение записи,
              напоминания за сутки и за два часа, отмена, перенос. Здесь же тихие часы —
              время, в которое сообщения не уходят.
            </p>
            <div className={styles["chips"]}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  props.onOpen("/settings/notifications")
                }}
              >
                Открыть
              </Button>
            </div>
          </div>
        ) : null}

        {canSeeTemplates ? (
          <div className={styles["block"]}>
            <div className={styles["blockTitle"]}>Шаблоны сообщений</div>
            <p className={styles["consequence"]}>
              То, что прочитает ваш клиент: напоминание, подтверждение, отмена. Наш текст
              вычитан юристом — свой можно вернуть к нему одной кнопкой. Здесь же видно,
              во сколько частей SMS укладывается текст и во что это обойдётся.
            </p>
            <div className={styles["chips"]}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  props.onOpen("/settings/templates")
                }}
              >
                Открыть
              </Button>
            </div>
          </div>
        ) : null}

        {canManageRoles ? (
          <div className={styles["block"]}>
            <div className={styles["blockTitle"]}>Роли и права</div>
            <p className={styles["consequence"]}>
              Что видит и может каждая роль: журнал, клиенты, деньги, выгрузки. Системные
              роли можно переименовать и поправить, свою — сделать копией готовой.
            </p>
            <div className={styles["chips"]}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  props.onOpen("/settings/roles")
                }}
              >
                Открыть
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
